import { callApi } from "./apiHelper";
import { apiEndpoints } from "./apiEndpoints";

interface UserRoleResponse {
  Role: string;
}

export interface User {
  UserId: string;
  FullName: string;
  PhoneNumber: string;
  Role: number;
  Status: boolean;
}

export interface UserResponse {
  users: User[];
}

export async function getCompanyUsers(role: Number = -1) {
  const response = await callApi<UserResponse>(
    apiEndpoints.getAllVisibleUsers,
    "POST",
    {
      operation: "getAllVisibleUsers",
    },
  );
  if (response.data === null) return [];
  if (role !== -1)
    return response.data.users.filter((user) => user.Role === role);
  return response.data;
}
// Raw Materials data types
export interface PurchaseOrder {
  PurchaseOrderId: string;
  VendorId: string;
  VendorName: string;
  TotalPrice: number;
  Status: string;
  CreatedAt: number;
  UpdatedAt: number;
  RawMaterials: RawMaterial[];
  HtmlUrl: string;
}
// Represents a unit conversion for a raw material
export interface UnitConversion {
  unit: string;
  conversionFactor: number;
}

// Represents a single raw material
export interface RawMaterial {
  // Core identifiers
  RawMaterialId: string;
  CompanyId: string;
  Code: string;
  Name: string;

  // Material characteristics
  RawMaterialType: string;
  BaseUnit: string;
  UnitConversions?: UnitConversion[];
  Price: number;
  Description: string;

  // Metadata
  CreatedAt: number; // Unix timestamp
  UpdatedAt: number; // Unix timestamp
  Visibility: boolean;
}

// Response structure from the API
export interface RawMaterialsResponse {
  message: string;
  rawMaterials: RawMaterial[];
}

// Unit types supported by the system
export type UnitType =
  | "Kg"
  | "g"
  | "mg"
  | "oz" // Weight units
  | "mtr"
  | "mm"
  | "cm"
  | "in" // Length units
  | "ltr"
  | "ml" // Volume units
  | "Pcs" // Piece units
  | "mt^2"
  | "mm^2"
  | "cm^2"
  | "in^2" // Area units
  | "sheet" // Sheet units
  | "pairs" // Pair units
  | "kg/mt^3"
  | "g/mt^3"
  | "g/ltr"
  | "kg/cm^3"; // Density units

// Unit categories for grouping compatible units
export enum UnitCategory {
  WEIGHT = "WEIGHT",
  LENGTH = "LENGTH",
  VOLUME = "VOLUME",
  PIECE = "PIECE",
  AREA = "AREA",
  SHEET = "SHEET",
  PAIR = "PAIR",
  DENSITY = "DENSITY",
  CUSTOM = "CUSTOM",
}

// Maps units to their categories
export const unitCategoryMap: Record<UnitType, UnitCategory> = {
  Kg: UnitCategory.WEIGHT,
  g: UnitCategory.WEIGHT,
  mg: UnitCategory.WEIGHT,
  oz: UnitCategory.WEIGHT,
  mtr: UnitCategory.LENGTH,
  mm: UnitCategory.LENGTH,
  cm: UnitCategory.LENGTH,
  in: UnitCategory.LENGTH,
  ltr: UnitCategory.VOLUME,
  ml: UnitCategory.VOLUME,
  Pcs: UnitCategory.PIECE,
  "mt^2": UnitCategory.AREA,
  "mm^2": UnitCategory.AREA,
  "cm^2": UnitCategory.AREA,
  "in^2": UnitCategory.AREA,
  sheet: UnitCategory.SHEET,
  pairs: UnitCategory.PAIR,
  "kg/mt^3": UnitCategory.DENSITY,
  "g/mt^3": UnitCategory.DENSITY,
  "g/ltr": UnitCategory.DENSITY,
  "kg/cm^3": UnitCategory.DENSITY,
};

// Standard conversion factors for compatible units (relative to base unit in each category)
export const standardConversions: Record<
  UnitCategory,
  Record<string, number>
> = {
  [UnitCategory.WEIGHT]: {
    Kg: 1,
    g: 1000,
    mg: 1000000,
    oz: 35.274,
  },
  [UnitCategory.LENGTH]: {
    mtr: 1,
    cm: 100,
    mm: 1000,
    in: 39.37,
  },
  [UnitCategory.VOLUME]: {
    ltr: 1,
    ml: 1000,
  },
  [UnitCategory.PIECE]: {
    Pcs: 1,
  },
  [UnitCategory.AREA]: {
    "mt^2": 1,
    "cm^2": 10000,
    "mm^2": 1000000,
    "in^2": 1550,
  },
  [UnitCategory.SHEET]: {
    sheet: 1,
  },
  [UnitCategory.PAIR]: {
    pairs: 1,
  },
  [UnitCategory.DENSITY]: {
    "kg/mt^3": 1,
    "g/mt^3": 1000,
    "g/ltr": 1,
    "kg/cm^3": 0.000001,
  },
  [UnitCategory.CUSTOM]: {},
};

// Interface for the unit converter component props
export interface UnitConverterProps {
  mode: "add" | "convert"; // Mode for adding new conversions or using existing ones
  baseUnit: UnitType; // The base unit of measurement
  baseValue: number; // The value in base unit
  unitConversions?: UnitConversion[]; // Custom unit conversions
  onChange?: (value: number, unit: string) => void; // Callback when value changes
  onAddConversion?: (conversion: UnitConversion) => void; // Callback when new conversion is added
}

export async function getRawMaterials() {
  const response = await callApi<RawMaterialsResponse>(
    apiEndpoints.getAllVisibleRawMaterials,
    "GET",
  );
  return response.data;
}

// SKU data types
export interface Sku {
  // Core identifiers
  SkuId: string;
  CompanyId: string;
  Code: string;
  Name: string;
  
  // SKU characteristics
  Attributes: Record<string, any>;
  BaseUnit: UnitType;
  Description: string;
  Images: string[];
  NewRelease: boolean;
  Price: number;
  Priority: number;
  Status: string;
  Units: Record<string, number>;
  SkuPerPieceLimit: Record<string, number>; // Raw material consumption limits
  UnitConversions?: UnitConversion[];
  ParentSkuId?: string;
  
  // Metadata
  CreatedAt: number; // Unix timestamp
  UpdatedAt: number; // Unix timestamp
  Visibility: boolean;
}

// Response structure from the SKU API
export interface SkuResponse {
  message: string;
  skus: Sku[];
}

export async function getVisibleSKUs() {
  return callApi<SkuResponse>(apiEndpoints.NulookGetSku);
}

export async function getAllSkus() {
  return callApi<SkuResponse>(
    apiEndpoints.NulookSkuManagement,
    "POST",
    {
      operation: "get_all"
    }
  );
}

export async function createSku(skuData: Omit<Sku, 'SkuId' | 'CompanyId' | 'CreatedAt' | 'UpdatedAt'>) {
  return callApi(
    apiEndpoints.NulookSkuManagement,
    "POST",
    {
      operation: "create",
      ...skuData
    }
  );
}

export async function updateSku(skuData: Partial<Sku> & { SkuId: string }) {
  return callApi(
    apiEndpoints.NulookSkuManagement,
    "POST",
    {
      operation: "update",
      ...skuData
    }
  );
}

export interface Notification {
  NotificationId: string;
  CompanyId: string;
  CreatedAt: number;
  CreatedBy: string;
  DataDump: string;
  NotificationFor: string;
  Status: string;
}

export interface NotificationResponse {
  message: string;
  notifications: Notification[];
}

export const getNotificationType = (dataDump: string): string => {
  try {
    const parsedData = JSON.parse(dataDump);
    if ("PurchaseOrderId" in parsedData) {
      return "Accept Purchase Order";
    } else if (parsedData.RequestType === "increase wastage") {
      return "Wastage Increase";
    } else if (parsedData.RequestType === "decrease wastage") {
      return "Wastage Decrease";
    }
    return "Unknown";
  } catch {
    return "Invalid";
  }
};

export async function getNotifications() {
  const response = await callApi<NotificationResponse>(
    apiEndpoints.NulookGetUserNotifications,
    "GET",
  );
  return response.data;
}

export async function getUserRole() {
  try {
    const response = await callApi<UserRoleResponse>(
      apiEndpoints.NulookGetUserRole,
    );

    if (response.error) {
      throw new Error(response.error.message);
    }

    if (!response.data) {
      throw new Error("No data received from the server");
    }

    return response.data.Role;
  } catch (error) {
    console.error("Error fetching user role:", error);
    throw error;
  }
}
